import { getIdToken } from "./auth.js";

/**
 * Centralized magic-link creation — THE one code path for every entry point
 * (right-clicked folder, single file, checkbox/marquee group). The scope model
 * is deliberately independent of the folder structure: whatever the final
 * public/private layout ends up being, anything resident in the library can be
 * magic-linked. Server side is POST /admin/share (Entra ID token + DamAdmin,
 * office-IP-gated) which mints the token and returns the shareable URL.
 */
export type MagicLinkScope =
  | { kind: "folder"; folder: string }
  | { kind: "assets"; assetIds: string[] };

export interface MagicLinkResult {
  url: string;
  token: string;
}

export async function createMagicLink(
  scope: MagicLinkScope,
  label: string,
): Promise<MagicLinkResult> {
  const idToken = await getIdToken();
  const body =
    scope.kind === "folder"
      ? { folder: scope.folder, label }
      : { assetIds: scope.assetIds, label };
  const res = await fetch("/admin/share", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as
    | { ok?: boolean; url?: string; token?: string; error?: string }
    | null;
  if (!res.ok || !json?.ok || !json.url || !json.token) {
    throw new Error(json?.error ?? `magic link request failed (${res.status})`);
  }
  return { url: json.url, token: json.token };
}

/** Human description of a scope, for the modal ("this folder", "6 files"). */
export function describeScope(scope: MagicLinkScope): string {
  if (scope.kind === "folder") {
    const name = scope.folder.split("/").pop() || scope.folder;
    return `the folder “${name}” (including subfolders)`;
  }
  return scope.assetIds.length === 1 ? "1 file" : `${scope.assetIds.length} files`;
}
